import React, { useState } from 'react'
import { CampaignList } from './CampaignList'
import { CampaignForm, FormValues } from './CampaignForm'
import { useCreateCampaign } from 'presentation/hooks/campaign/useCreateCampaign'
import { useUpdateCampaign } from 'presentation/hooks/campaign/useUpdateCampaign'
import { Campaign } from 'domain/entities/Campaign'
import { Inventory } from 'domain/entities/Inventory'
import { useInventoryListsForCampaign } from 'presentation/hooks/campaign/useGetAvailableInventoryList'
import { useCreateTag } from 'presentation/hooks/tag/useCreateTag'
import { useDeleteTag } from 'presentation/hooks/tag/useDeleteTag'
import { Button } from '../../components/Button/Button'

export const CampaignsPage: React.FC = () => {
  const [creating, setCreating] = useState(false)
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null)
  const [availableInventoryList, setAvailableInventoryList] = useState<
    Inventory[]
  >([])
  const [connectedInventoryList, setConnectedInventoryList] = useState<
    Inventory[]
  >([])

  const { createCampaign, error } = useCreateCampaign()
  const { updateCampaign } = useUpdateCampaign()
  const { deleteTag } = useDeleteTag()
  const { createTag } = useCreateTag()

  const { loadInventoryListsCallback, isLoading } =
    useInventoryListsForCampaign()

  const handleSubmit = async (values: FormValues) => {
    if (editingCampaign) {
      await updateCampaign(editingCampaign.id, values)
      return editingCampaign.id
    } else {
      const id = await createCampaign(values)
      if (error) {
        throw new Error('Failed to create campaign')
      }
      return id
    }
  }

  const handleCancel = () => {
    setCreating(false)
    setEditingCampaign(null)
    setAvailableInventoryList([])
    setConnectedInventoryList([])
  }

  const handleEdit = async (campaign: Campaign) => {
    setEditingCampaign(campaign)
    const { available, connected } = await loadInventoryListsCallback(
      campaign.id
    )
    setAvailableInventoryList(available)
    setConnectedInventoryList(connected)
  }

  const handleInventoryUnlink = (campaignID: string, inventoryID: string) => {
    deleteTag(campaignID, inventoryID)
  }

  const handleInventoryLink = (campaignID: string, inventoryID: string) => {
    createTag({
      name: `${inventoryID}-${campaignID}`,
      campaignID,
      inventoryID,
    })
  }

  const handleCreate = async () => {
    setCreating(true)
    const { available } = await loadInventoryListsCallback(undefined)
    setAvailableInventoryList(available)
    setConnectedInventoryList([])
  }

  return (
    <div>
      {(creating || editingCampaign) && (
        <Button type="button" variant="back" onClick={handleCancel}>
          Back to campaign list
        </Button>
      )}

      <h1>Campaigns</h1>

      {creating || editingCampaign ? (
        <CampaignForm
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          initialData={
            editingCampaign && {
              id: editingCampaign.id,
              title: editingCampaign.title,
              vastUrl: editingCampaign.vastUrl,
              status: editingCampaign.status,
            }
          }
          loading={isLoading}
          connectedInventoryList={connectedInventoryList}
          availableInventoryList={availableInventoryList}
          onInventoryUnlink={handleInventoryUnlink}
          onInventoryLink={handleInventoryLink}
        />
      ) : (
        <>
          <button className="btn-primary" onClick={handleCreate}>
            Create Campaign
          </button>
          <CampaignList onEdit={handleEdit} />
        </>
      )}
    </div>
  )
}
